import React, { useMemo, useEffect, useState } from 'react';
import style from './style.module.css';
import { IBookingCardCompact } from './types';
import DropDownHeader from 'design-v2/common/DropDownHeader';
import { ListOption } from 'design-v2/common/RadioList';
import { IBookingStatus } from 'shared-types/index';
import classNames from 'classnames';
import IconsService from 'external-services/icons-service/icons.service';
import { IconTypes } from 'external-services/icons-service/icons.types';
import TagIcon from 'external-components/tag-icon/index';
import AdminService from 'external-services/diary-admin-service/admin.service';
import ModalContainer from 'design-v2/common/ModalContainer';
import AlertPanel from 'external-components/alert-panel';
import PersonIcon from '@material-ui/icons/Person';
import LinkIcon from '@material-ui/icons/Link';
import PaxInput from 'design-v2/common/PaxInput';
import StatusSymbol, { StatusTitleRender } from 'design-v2/common/StatusSymbol';
import CustomerLookupService from 'external-services/customer-lookup-service/index';
import colors from 'design-v2/globals/colors/colors.module.css';

const NS = 'BookingCardCompactV2';

export default function BookingCardCompactV2(props: IBookingCardCompact) {
  const {
    isEditable,
    canSave = true,
    cantSaveMessageList = [],
    staffRequired = false,
    historicalBookingStatus,
    handleStatus,
    handleStatusChange,
    StaffPanel,
    isWideVersion,
    isQuickEditLocked,
    handlePaxSave,
    saveAttempted,
    handleSaveAttempted,
    parentWidth,
    handleOpenPaxModal,
    handleChangePax,
    handleCancelModal,
    handleClosePaxModal = null,
    bookingType,
    paymentDetails,
    staffEditQuickEdit,
    useDeprecatedStatusModal = true,
    handleBookingStatusClicked,
    handleBookingCardListPointerDown,
    isOverTableCapacity,
    enableEndBookingTime,
    customerId,
    hasCustomerNotes,
    bookingNotes,
    showDropdownIconInStatus= true,
    showDateInBookingCard = false,
    bookingDate,
  } = props;
  // const CustomerIcon = IconsService.getIcon(IconTypes.customers)
  // @ts-ignore
  const TableIcon = IconsService.getIcon(IconTypes.table);
  const TIcon = IconsService.getIcon(IconTypes.tIcon);

  const [showList, setShowList] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<number>(props.selectedIndex);
  const [quickStatus, setQuickStatus] = useState<number>(props.selectedIndex);
  const [showPaxModal, setShowPaxModal] = useState<boolean>(false);
  const [showTableTooltip, setShowTableTooltip] = useState<boolean>(false);

  useEffect(() => {
    if (!showPaxModal) {
      handleClosePaxModal && handleClosePaxModal();
    }
  }, [showPaxModal]);

  const firstThreeTags = useMemo(() => {
    const _tags = props.tags.slice(0, 3);
    if (_tags.length && _tags[0].name === 'Notes') {
      const firstElement = _tags.shift();
      _tags.push(firstElement);
    }
    return _tags
  }, [props.tags]);

  const isPartnerBookingsLocked = useMemo(() => {
    return AdminService.isPartnerBookingsLocked(props.functionTracker);
  }, [props.functionTracker]);

  const quickStatusHandler = () => {
    if (isQuickEditLocked || !isEditable) {
      return;
    }
    if (!isPartnerBookingsLocked && !props.bookingStatusChangeDisable) {
      if (useDeprecatedStatusModal) {
        setShowList(!showList);
        !showList && handleStatus && handleStatus();
      } else {
        handleBookingStatusClicked && handleBookingStatusClicked(props.bookingStatusList[props.selectedIndex]._id);
      }
    }
  };
  const quickPaxHandler = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isQuickEditLocked || !isEditable) {
      return;
    }
    handleOpenPaxModal(props.covers);
    setShowPaxModal(!showPaxModal);
  };

  const handleListItemClick = (index: number) => {
    setQuickStatus(index);
    showList && handleStatus && handleStatus(props.bookingStatusList[index], true);
  };
  const saveButtonHandler = () => {
    if (canSave) {
      setSelectedStatus(quickStatus);
      handleStatusChange && handleStatusChange(props.bookingStatusList[quickStatus].name);
      setShowList(false);
    } else {
      !!handleSaveAttempted && handleSaveAttempted();
    }
  };
  const cancelButtonHandler = () => {
    setShowList(false);
    setQuickStatus(props.selectedIndex);
    handleCancelModal && handleCancelModal();
  };

  const cancelPaxModalHandler = () => {
    setShowPaxModal(false);
    handleCancelModal && handleCancelModal();
  };

  const paxSaveButtonHandler = (value: number) => {
    if (canSave) {
      handlePaxSave && handlePaxSave(value);
      setShowPaxModal(false);
    } else {
      !!handleSaveAttempted && handleSaveAttempted();
    }
  };

  useEffect(() => {
    setSelectedStatus(props.selectedIndex);
    setQuickStatus(props.selectedIndex);
  }, [props.selectedIndex]);

  const dropDownItems: {
    title: string | JSX.Element;
    children: JSX.Element;
    colour?: string;
    tailTitle?: string;
    disabled: boolean;
  }[] = props.bookingStatusList.map((option: IBookingStatus, index: number) => {
    return {
      title: (
        <StatusTitleRender
          status={option}
          paymentDetails={paymentDetails}
          bookingType={bookingType}
        />
      ),
      children: (
        <StatusSymbol
          status={option}
          bookingType={bookingType}
          paymentDetails={paymentDetails}
          isModal={true}
        />
      ),
      disabled: false,
    };
  });

  const hasValidationErrors = !canSave;

  const nameWrapperContainerWidth = useMemo(() => {
    if (!parentWidth) return '100%';

    // 32: Status box width
    // 8: Padding Left and Right of this component
    return parentWidth - 32 - 8;
  }, [parentWidth]);

  const warningPanel = useMemo(() => {
    if (saveAttempted && !canSave) {
      return (
        <div className={style.alertWrap}>
          <AlertPanel
            severity="warning"
            message={`There are problems with your booking that need addressing before you can save:`}
            messageList={cantSaveMessageList}
          />
        </div>
      );
    }

    return null;
  }, [cantSaveMessageList, saveAttempted, canSave]);

  const bookingStatusSelected = props.bookingStatusList[selectedStatus];
  return (
    <div
      className={classNames({
        [style.root]: true,
        [style.isEditable]: isEditable,
        [style.ftBooking]: isPartnerBookingsLocked,
        [style.rootV2]: true
      })}
      data-testid="container"
      onPointerDown={handleBookingCardListPointerDown}
    >
      {selectedStatus > -1 && bookingStatusSelected && (
        <div className={style.statusBoxV2}>
          <div
            data-testid="status"
            className={classNames({
              [style.statusCapital]: true,
              [style.statusCapitalLocked]: isQuickEditLocked,
              [style.statusCapitalV2]: true,
            })}
            onClick={quickStatusHandler}
          >
            <StatusSymbol
              status={historicalBookingStatus}
              bookingType={bookingType}
              paymentDetails={paymentDetails}
              showDropdownIcon={showDropdownIconInStatus}
              isNarrowVersion={true}
            />
          </div>
        </div>
      )}

      {props.isModalStyle && (
        <ModalContainer
          closeTimeoutMS={700}
          isOpen={showList}
          onRequestClose={cancelButtonHandler}
          contentStyle={isWideVersion && { minWidth: '400px', inset: '50% 50% auto 50%', maxWidth: '520px' }}
        >
          <DropDownHeader
            title={
              <StatusTitleRender
                status={bookingStatusSelected}
                paymentDetails={paymentDetails}
                bookingType={bookingType}
                isHeader={true}
              />
            }
            handleCancel={cancelButtonHandler}
            handleSave={saveButtonHandler}
            isColorOverride={hasValidationErrors}
          />
          {warningPanel}
          {dropDownItems.map((item, i) => {
            return (
              <ListOption
                dataTestId={`dropdown-option-${i}`}
                key={i}
                title={item.title}
                icon={item.children}
                index={i}
                tailTitle={item.tailTitle}
                onClick={() => {
                  if (!item.disabled) {
                    handleListItemClick(i);
                  }
                }}
                checked={quickStatus === i}
                hiddenBorder={i === dropDownItems.length - 1}
                disabled={item.disabled}
              />
            );
          })}
          {staffRequired && StaffPanel()}
        </ModalContainer>
      )}

      <div
        onClick={() => props.handleBookingClicked()}
        className={style.contentContainerV2}
      >

        <div className={style.contentContainerInMiddle}>
          <div className={style.flexRowSpaceBetween}>
            <div
              className={classNames({
                [style.bookTime]: true,
              })}
              style={{fontSize: 14, fontWeight: 500}}
              data-testid="time"
            >
              <span data-testid="book-time">
                {showDateInBookingCard && bookingDate && (bookingDate + ' ')}
                {`${props.startTimeValue}`}
                <span className={style.timeSmaller}>{`${props.startTimePeriod}`}</span>
                {enableEndBookingTime && <span> -
                <span> {`${props.endTimeValue}`}</span>
                <span className={style.timeSmaller}>{`${props.endTimePeriod}`}</span>
                </span>}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center'}}>
              {firstThreeTags.map((tag, i) => {
                let extendedProp = {};
                if (tag.name === 'Notes') {
                  extendedProp = {
                    customerId,
                    getCustomerInfo: (id: string) => CustomerLookupService.getById(id),
                    title: bookingNotes ? `Booking Notes: \n ${bookingNotes}` : '',
                    hasCustomerNotes,
                  };
                }
                return (
                  <span className={style.tagIconStyle} key={i + tag.name}>
                    <TagIcon {...tag} {...extendedProp} key={i + tag.name} isGrey={true}/>
                  </span>
                );
              })}
              {props.tags.length > 3 && (
                <span className={style.tagIconStyle} style={{fontSize: '12px', color: 'rgba(0, 0, 0, 0.5)', marginBottom: -2}}>
                  +{props.tags.length - 3}
                </span>
              )}
            </div>
          </div>
          <div
            className={classNames({
              [style.userName]: true,
              [style.ellipsisElement]: true,
              [style.userNameV2]: true
            })}
            data-testid="customer"
          >
            {props.company ? `${props.fullName}, ${props.company}` : `${props.fullName}`}
          </div>
        </div>

        <div className={style.contentContainerAtEnd}>
          <div
            className={classNames({
              [style.paxBoxCovers]: true,
              [style.paxBoxCoversLocked]: isQuickEditLocked,
              [style.isOverTableCapacity]: isOverTableCapacity,
              [style.coverBoxNoPadding]: true,
            })}
            onClick={quickPaxHandler}
          >
            <span className={style.flexVerticalContainer}>
              {props.covers < 100 && <PersonIcon className={style.iconStyleV2}> </PersonIcon>}
              <span style={{ fontSize: 12}}>{props.covers}</span>
            </span>
          </div>

          <div
            className={classNames({
              [style.tableAssign]: true,
              [style.tableContainerV2]: true
            })}
            data-testid="table"
          >
            <div
              className={classNames({
                [style.flexVerticalContainer]: true,
              })}
            >
              {props.tablesNames.length > 0 && (
                <div
                  className={classNames({
                    [style.flexVerticalContainer]: true,
                    [style.iconV2]: true
                  })}
                >
                  {props.tablesNames.length === 1 ? (
                    <TableIcon className={style.iconStyleV2} />
                  ) : (
                    <LinkIcon
                      style={{ fontSize: 16, cursor: 'pointer' }}
                      onMouseEnter={() => setShowTableTooltip(true)}
                      onMouseLeave={() => setShowTableTooltip(false)}
                      data-testid="link-icon"
                      className={colors.clrNbiMagenta}
                    />
                  )}
                </div>
              )}
              {props.tableAvailable && (
                <div
                  className={classNames({
                    [style.flexCenter]: true,
                    [style.icon]: true,
                    [style.tContainer]: true,
                  })}
                >
                  <TIcon className={style.tIconStyle} />
                </div>
              )}
              {props.tablesNames.length > 0 && (
                <div style={{ position: 'relative' }}>
                  <span style={{ fontSize: 12 }} data-testid="single-table-name">
                    {props.tablesNames[0].length > 4 ? props.tablesNames[0].substring(0, 4) : props.tablesNames[0]}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>


      </div>
      {showPaxModal && (
        <PaxInput
          showPaxList={showPaxModal}
          cancelButtonHandler={cancelPaxModalHandler}
          saveAttempted={saveAttempted}
          isColorOverride={cantSaveMessageList.length > 0}
          saveButtonHandler={paxSaveButtonHandler}
          coversPrefill={props.covers}
          showStaffName={staffEditQuickEdit}
          staffNameComponent={StaffPanel}
          handleChangePax={handleChangePax}
          warningComponent={warningPanel}
        />
      )}
    </div>
  );
}
